<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import unhappyCasesAPI from 'dashboard/api/unhappyCases';
import ReportHeader from './components/ReportHeader.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { startOfDay, endOfDay, subDays, getUnixTime } from 'date-fns';
import { useUnhappyCaseConstants } from 'dashboard/composables/useUnhappyCaseConstants';

const { t } = useI18n();
const { SIGNAL_LABELS } = useUnhappyCaseConstants();
const store = useStore();
const getters = useStoreGetters();

const isLoading = ref(false);
const summary = ref(null);
const timeseries = ref([]);
const breakdownDimension = ref('signal');
const breakdownData = ref([]);

const groupBy = ref('day');

// Date range — driven by WootDatePicker (emits [Date, Date])
const since = ref(getUnixTime(startOfDay(subDays(new Date(), 29))).toString());
const until = ref(getUnixTime(endOfDay(new Date())).toString());

const onDateRangeChange = ([start, end]) => {
  since.value = getUnixTime(start).toString();
  until.value = getUnixTime(end).toString();
};

const BREAKDOWN_TABS = [
  { value: 'signal', label: t('UNHAPPY_CASES.REPORTS.BREAKDOWN.BY_SIGNAL') },
  { value: 'agent', label: t('UNHAPPY_CASES.REPORTS.BREAKDOWN.BY_AGENT') },
  { value: 'inbox', label: t('UNHAPPY_CASES.REPORTS.BREAKDOWN.BY_INBOX') },
  {
    value: 'review_status',
    label: t('UNHAPPY_CASES.REPORTS.BREAKDOWN.BY_STATUS'),
  },
];

const GROUP_BY_OPTIONS = [
  { value: 'day', label: t('UNHAPPY_CASES.REPORTS.GROUP_BY.DAY') },
  { value: 'week', label: t('UNHAPPY_CASES.REPORTS.GROUP_BY.WEEK') },
  { value: 'month', label: t('UNHAPPY_CASES.REPORTS.GROUP_BY.MONTH') },
];

const SIGNAL_BAR_CLASSES = {
  low_csat: 'bg-n-ruby-9',
  reopen: 'bg-n-amber-9',
  slack_alerted: 'bg-n-iris-9',
  label: 'bg-n-teal-9',
  contact_reason: 'bg-n-slate-9',
};

const agents = computed(() => getters['agents/getAgents'].value || []);
const inboxes = computed(() => getters['inboxes/getInboxes'].value || []);

const dateParams = computed(() => ({
  since: since.value,
  until: until.value,
  group_by: groupBy.value,
  timezone_offset: new Date().getTimezoneOffset() / -60,
}));

const fetchAll = async () => {
  isLoading.value = true;
  try {
    const [summaryRes, timeseriesRes, breakdownRes] = await Promise.all([
      unhappyCasesAPI.reportSummary(dateParams.value),
      unhappyCasesAPI.reportTimeseries(dateParams.value),
      unhappyCasesAPI.reportBreakdown({
        ...dateParams.value,
        dimension: breakdownDimension.value,
      }),
    ]);
    summary.value = summaryRes.data;
    timeseries.value = timeseriesRes.data;
    breakdownData.value = breakdownRes.data;
  } catch {
    summary.value = null;
    timeseries.value = [];
    breakdownData.value = [];
  } finally {
    isLoading.value = false;
  }
};

const fetchBreakdown = async () => {
  try {
    const { data } = await unhappyCasesAPI.reportBreakdown({
      ...dateParams.value,
      dimension: breakdownDimension.value,
    });
    breakdownData.value = data;
  } catch {
    breakdownData.value = [];
  }
};

const onBreakdownTabChange = dim => {
  breakdownDimension.value = dim;
  fetchBreakdown();
};

const resolveBreakdownLabel = item => {
  if (breakdownDimension.value === 'signal') {
    return SIGNAL_LABELS[item.label] || item.label;
  }
  if (breakdownDimension.value === 'agent') {
    return (
      agents.value.find(a => a.id === item.id)?.name ||
      t('UNHAPPY_CASES.REPORTS.BREAKDOWN.UNASSIGNED')
    );
  }
  if (breakdownDimension.value === 'inbox') {
    return (
      inboxes.value.find(i => i.id === item.id)?.name ||
      t('UNHAPPY_CASES.REPORTS.BREAKDOWN.UNKNOWN')
    );
  }
  const label = item.label || String(item.id);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const breakdownBarClass = item => {
  if (breakdownDimension.value === 'signal') {
    return SIGNAL_BAR_CLASSES[item.label] || 'bg-woot-500';
  }
  if (breakdownDimension.value === 'review_status') {
    return (
      {
        pending: 'bg-n-amber-9',
        confirmed: 'bg-n-teal-9',
        rejected: 'bg-n-ruby-9',
      }[item.label] || 'bg-woot-500'
    );
  }
  return 'bg-woot-500';
};

const sortedBreakdownData = computed(() => {
  if (!['agent', 'inbox'].includes(breakdownDimension.value)) {
    return breakdownData.value;
  }
  return [...breakdownData.value].sort((a, b) => {
    const la = resolveBreakdownLabel(a).toLowerCase();
    const lb = resolveBreakdownLabel(b).toLowerCase();
    return la.localeCompare(lb);
  });
});

const totalBreakdown = computed(() =>
  breakdownData.value.reduce((sum, b) => sum + b.count, 0)
);

const formatDate = ts =>
  new Date(ts * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

// Trend color: compare avg of first half vs second half of the series.
// Increasing unhappy cases = bad (red), decreasing = good (green), flat = orange.
const TREND_COLORS = {
  green: '#10b981',
  orange: '#f59e0b',
  red: '#ef4444',
};

const trendColor = computed(() => {
  const data = timeseries.value.map(p => p.value);
  if (data.length < 2) return TREND_COLORS.orange;
  const mid = Math.ceil(data.length / 2);
  const avg = arr => arr.reduce((a, b) => a + b, 0) / arr.length;
  const delta = avg(data.slice(mid)) - avg(data.slice(0, mid));
  if (delta > 0.5) return TREND_COLORS.red;
  if (delta < -0.5) return TREND_COLORS.green;
  return TREND_COLORS.orange;
});

const trendChartCollection = computed(() => ({
  labels: timeseries.value.map(p => formatDate(p.timestamp)),
  datasets: [
    {
      label: t('UNHAPPY_CASES.REPORTS.TREND.TITLE'),
      data: timeseries.value.map(p => p.value),
      borderColor: trendColor.value,
      backgroundColor: 'transparent',
      borderWidth: 2,
      fill: false,
      pointBackgroundColor: trendColor.value,
      pointRadius: 3,
      pointHoverRadius: 5,
    },
  ],
}));

const trendChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: {
      grid: { drawOnChartArea: false },
      ticks: { maxRotation: 45, font: { size: 11 } },
    },
    y: {
      beginAtZero: true,
      grid: { drawOnChartArea: false },
      ticks: { stepSize: 1, font: { size: 11 } },
    },
  },
};

const formatPct = v => (v == null ? '—' : `${v}%`);
const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);

const aiQualityColor = computed(() => {
  const avg = summary.value?.ai_quality_avg;
  if (avg == null)
    return {
      value: 'text-n-slate-12',
      progress: 'bg-n-slate-5',
      icon: 'text-n-slate-9',
      bg: 'bg-n-alpha-2',
    };
  if (avg >= 60)
    return {
      value: 'text-n-teal-11',
      progress: 'bg-n-teal-8',
      icon: 'text-n-teal-9',
      bg: 'bg-n-teal-3',
    };
  if (avg >= 40)
    return {
      value: 'text-n-amber-11',
      progress: 'bg-n-amber-8',
      icon: 'text-n-amber-9',
      bg: 'bg-n-amber-3',
    };
  return {
    value: 'text-n-ruby-11',
    progress: 'bg-n-ruby-8',
    icon: 'text-n-ruby-9',
    bg: 'bg-n-ruby-3',
  };
});

// Summary cards config
const SUMMARY_CARDS = computed(() => [
  {
    key: 'total',
    label: t('UNHAPPY_CASES.REPORTS.SUMMARY.TOTAL'),
    value: summary.value?.total ?? '—',
    icon: 'i-lucide-triangle-alert',
    iconClass: 'text-n-ruby-9',
    bgClass: 'bg-n-ruby-3',
    valueClass: 'text-n-slate-12',
  },
  {
    key: 'pending',
    label: t('UNHAPPY_CASES.REPORTS.SUMMARY.PENDING'),
    value: summary.value?.pending ?? '—',
    icon: 'i-lucide-clock',
    iconClass: 'text-n-amber-9',
    bgClass: 'bg-n-amber-3',
    valueClass: 'text-n-amber-11',
  },
  {
    key: 'confirmed',
    label: t('UNHAPPY_CASES.REPORTS.SUMMARY.CONFIRMED'),
    value: summary.value?.confirmed ?? '—',
    icon: 'i-lucide-check-circle',
    iconClass: 'text-n-teal-9',
    bgClass: 'bg-n-teal-3',
    valueClass: 'text-n-teal-11',
  },
  {
    key: 'trusted',
    label: t('UNHAPPY_CASES.REPORTS.SUMMARY.TRUSTED'),
    value: summary.value?.trusted ?? '—',
    icon: 'i-lucide-shield-check',
    iconClass: 'text-n-iris-9',
    bgClass: 'bg-n-iris-3',
    valueClass: 'text-n-iris-11',
  },
  {
    key: 'review_coverage',
    label: t('UNHAPPY_CASES.REPORTS.SUMMARY.REVIEW_COVERAGE'),
    value: formatPct(summary.value?.review_coverage),
    progress: summary.value?.review_coverage,
    progressClass: 'bg-woot-500',
    icon: 'i-lucide-activity',
    iconClass: 'text-woot-500',
    bgClass: 'bg-woot-50 dark:bg-woot-900/20',
    valueClass: 'text-n-slate-12',
  },
  {
    key: 'ai_quality',
    label: t('UNHAPPY_CASES.REPORTS.SUMMARY.AI_QUALITY'),
    value: formatPct(summary.value?.ai_quality_avg),
    progress: summary.value?.ai_quality_avg,
    progressClass: aiQualityColor.value.progress,
    good: summary.value?.ai_quality_good ?? 0,
    bad: summary.value?.ai_quality_bad ?? 0,
    icon: 'i-lucide-sparkles',
    iconClass: aiQualityColor.value.icon,
    bgClass: aiQualityColor.value.bg,
    valueClass: aiQualityColor.value.value,
  },
]);

onBeforeMount(() => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  fetchAll();
});
</script>

<template>
  <ReportHeader
    :header-title="$t('UNHAPPY_CASES.REPORTS.TITLE')"
    :header-description="$t('UNHAPPY_CASES.REPORTS.DESCRIPTION')"
  />

  <!-- Filters -->
  <div
    class="mb-6 p-4 rounded-xl border border-n-weak bg-n-solid-2 flex items-center gap-4 flex-wrap"
  >
    <WootDatePicker @date-range-changed="onDateRangeChange" />
    <div class="flex items-center gap-2 ml-auto">
      <span
        class="text-xs font-medium text-n-slate-9 uppercase tracking-wider shrink-0"
      >
        {{ $t('UNHAPPY_CASES.REPORTS.TREND.TITLE') }}
      </span>
      <div class="flex rounded-lg border border-n-weak overflow-hidden">
        <button
          v-for="opt in GROUP_BY_OPTIONS"
          :key="opt.value"
          type="button"
          class="px-3 py-1.5 text-xs font-medium transition-colors"
          :class="
            groupBy === opt.value
              ? 'bg-woot-500 text-white'
              : 'bg-n-background text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="groupBy = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>
      <button
        type="button"
        class="px-4 py-1.5 text-sm font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 transition-colors"
        :disabled="isLoading"
        @click="fetchAll"
      >
        {{ $t('UNHAPPY_CASES.REPORTS.APPLY') }}
      </button>
    </div>
  </div>

  <div class="flex flex-col gap-8">
    <!-- Summary skeleton -->
    <section v-if="isLoading">
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div
          v-for="n in 6"
          :key="n"
          class="rounded-xl border border-n-weak bg-n-solid-2 p-4 flex flex-col gap-3"
        >
          <div class="flex items-center gap-2">
            <div
              class="w-7 h-7 rounded-lg bg-n-slate-3 animate-pulse shrink-0"
            />
            <div class="w-24 h-3.5 rounded bg-n-slate-3 animate-pulse" />
          </div>
          <div class="w-14 h-8 rounded-md bg-n-slate-3 animate-pulse" />
        </div>
      </div>
    </section>

    <!-- Summary cards -->
    <section v-else-if="summary">
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div
          v-for="card in SUMMARY_CARDS"
          :key="card.key"
          class="rounded-xl border border-n-weak bg-n-solid-2 p-4 flex flex-col gap-2"
        >
          <!-- Icon + label -->
          <div class="flex items-center gap-2">
            <span
              class="flex items-center justify-center w-7 h-7 rounded-lg shrink-0"
              :class="card.bgClass"
            >
              <span class="size-3.5" :class="[card.icon, card.iconClass]" />
            </span>
            <span class="text-xs font-medium text-n-slate-10 leading-tight">
              {{ card.label }}
            </span>
          </div>
          <!-- Value -->
          <div class="flex items-center justify-between">
            <span class="text-2xl font-semibold" :class="card.valueClass">
              {{ card.value }}
            </span>
            <!-- Good / Bad chips for ai_quality card -->
            <div
              v-if="card.key === 'ai_quality'"
              class="flex items-center gap-1.5 text-xs"
            >
              <span
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11 font-medium"
              >
                <span class="i-lucide-thumbs-up size-3" />
                {{ card.good }}
              </span>
              <span
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-n-ruby-3 text-n-ruby-11 font-medium"
              >
                <span class="i-lucide-thumbs-down size-3" />
                {{ card.bad }}
              </span>
            </div>
          </div>
          <!-- Progress bar for review coverage and ai_quality -->
          <div
            v-if="card.progress != null"
            class="h-1 rounded-full bg-n-alpha-2 overflow-hidden mt-auto"
          >
            <div
              class="h-1 rounded-full transition-all duration-500"
              :class="card.progressClass"
              :style="{ width: `${card.progress}%` }"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Breakdown -->
    <section v-if="!isLoading">
      <div class="flex items-center justify-between mb-3">
        <h3
          class="text-sm font-semibold text-n-slate-11 uppercase tracking-wider"
        >
          {{ $t('UNHAPPY_CASES.REPORTS.BREAKDOWN.TITLE') }}
        </h3>
        <div class="flex rounded-lg border border-n-weak overflow-hidden">
          <button
            v-for="tab in BREAKDOWN_TABS"
            :key="tab.value"
            type="button"
            class="px-3 py-1.5 text-xs transition-colors"
            :class="
              breakdownDimension === tab.value
                ? 'bg-woot-500 text-white'
                : 'bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="onBreakdownTabChange(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>
      <div
        v-if="sortedBreakdownData.length"
        class="rounded-xl border border-n-weak bg-n-solid-2 divide-y divide-n-weak overflow-hidden"
      >
        <div
          v-for="(item, idx) in sortedBreakdownData"
          :key="idx"
          class="flex items-center gap-4 px-4 py-3"
        >
          <span class="text-sm text-n-slate-12 w-44 shrink-0 truncate">
            {{ resolveBreakdownLabel(item) }}
          </span>
          <div class="flex-1 bg-n-alpha-2 rounded-full h-2 overflow-hidden">
            <div
              class="h-2 rounded-full transition-all duration-500"
              :class="breakdownBarClass(item)"
              :style="{
                width: totalBreakdown
                  ? `${pct(item.count, totalBreakdown)}%`
                  : '0%',
              }"
            />
          </div>
          <span class="text-xs text-n-slate-9 w-8 text-right shrink-0">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            {{ pct(item.count, totalBreakdown) }}%
          </span>
          <span
            class="text-sm font-semibold text-n-slate-12 w-8 text-right shrink-0"
          >
            {{ item.count }}
          </span>
        </div>
      </div>
      <p v-else class="text-sm text-n-slate-9 py-8 text-center">
        {{ $t('UNHAPPY_CASES.REPORTS.EMPTY') }}
      </p>
    </section>

    <!-- Trend -->

    <section v-if="!isLoading && timeseries.length">
      <h3
        class="text-sm font-semibold text-n-slate-11 uppercase tracking-wider mb-3"
      >
        {{ $t('UNHAPPY_CASES.REPORTS.TREND.TITLE') }}
      </h3>
      <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4 h-72">
        <LineChart
          :collection="trendChartCollection"
          :chart-options="trendChartOptions"
        />
      </div>
    </section>

    <p
      v-if="!isLoading && !summary"
      class="text-sm text-n-slate-9 py-8 text-center"
    >
      {{ $t('UNHAPPY_CASES.REPORTS.EMPTY') }}
    </p>
  </div>
</template>
